import type { PublicSummary } from "@/v2-status-client/status/status.types"
import {
  formatLatencyMs,
  formatRelativeTime,
} from "@/v2-status-client/status/status.utils"
import type { StatusKpiTone } from "@/v2-status-client/status/kpis/StatusKpiTile"

// One tile descriptor. Pre-computed so `StatusKpis.tsx` stays a
// thin map over the array; all the wording / formatting / tone
// rules live here next to the data shapes they operate on.
export type StatusKpiTileSpec = {
  id: string
  label: string
  value: string
  sub: string
  tone: StatusKpiTone
}

// Build the four tiles rendered in the KPI strip. Mirrors the
// vocabulary of the operator-side dashboard cards (services /
// uptime / response / incidents) so customers see numbers we
// already trust internally, just rewritten without the
// operator-only nouns ("targets", "drift", "actionable").
export function buildStatusKpiTiles(s: PublicSummary): StatusKpiTileSpec[] {
  return [
    buildServicesMonitoredTile(s),
    buildUptimeTile(s),
    buildAvgResponseTile(s),
    buildIncidentsTile(s),
  ]
}

// "Services monitored": total fleet size up top, with the per-
// segment breakdown ("Placepoint apps: 24/28 healthy") on the sub
// so the most customer-relevant slice surfaces without taking a
// whole tile. Tone stays neutral -- this is a count, not a
// status, and we don't want to alarm-tint the top of the page.
function buildServicesMonitoredTile(s: PublicSummary): StatusKpiTileSpec {
  const sub =
    s.coreTotalServices > 0
      ? `Placepoint apps: ${s.coreServicesUp}/${s.coreTotalServices} healthy`
      : `${s.servicesUp} responding`
  return {
    id: "services",
    label: "Services monitored",
    value: formatInteger(s.totalServices),
    sub,
    tone: "neutral",
  }
}

// "Placepoint uptime · 30 d": the headline reliability number,
// scoped to services we actually operate. We deliberately don't
// pool in 80+ third-party data layers here -- a customer should
// see Placepoint's reliability without it being dragged around
// by upstream outages we can't fix.
function buildUptimeTile(s: PublicSummary): StatusKpiTileSpec {
  return {
    id: "uptime",
    label: "Placepoint uptime · 30 d",
    value:
      s.uptimePercent30d != null ? formatPercentExact(s.uptimePercent30d) : "—",
    sub: "across services we operate",
    tone: "neutral",
  }
}

// "Avg response time": headline performance number. Prefers the
// live 24h average; the backend transparently falls back to the
// 90d baseline when 24h is empty and flags it via the
// `avgLatencyIsBaseline` field. The sub-line tracks that flag so
// the tile always tells the customer which window the number
// actually represents.
function buildAvgResponseTile(s: PublicSummary): StatusKpiTileSpec {
  const value = formatLatencyMs(s.avgLatencyMs)
  let sub: string
  if (s.avgLatencyMs == null) {
    sub = "no probe samples available"
  } else if (s.avgLatencyIsBaseline) {
    sub = "90-day baseline · no 24 h samples yet"
  } else {
    sub = "across operational services · last 24 h"
  }
  return {
    id: "latency",
    label: "Avg response time",
    value,
    sub,
    tone: "neutral",
  }
}

// "Recent incidents": last-30d count + last-incident relative
// time. Stays neutral by default; only a currently-ongoing
// incident upgrades the tone to warning so a customer landing
// during an active event doesn't have to scroll to notice.
function buildIncidentsTile(s: PublicSummary): StatusKpiTileSpec {
  let sub: string
  if (s.incidentsOngoing > 0) {
    sub =
      s.incidentsOngoing === 1
        ? "1 incident in progress"
        : `${s.incidentsOngoing} incidents in progress`
  } else if (s.incidentsLast30d === 0) {
    sub = "no incidents in the last 30 days"
  } else {
    const ago = formatRelativeTime(s.lastIncidentAt)
    sub = ago ? `last incident ${ago}` : "all resolved"
  }
  const tone: StatusKpiTone = s.incidentsOngoing > 0 ? "warning" : "neutral"
  return {
    id: "incidents",
    label: "Incidents · 30 d",
    value: formatInteger(s.incidentsLast30d),
    sub,
    tone,
  }
}

// Plain integer with thousand separators. Browsers handle this
// natively; we keep the helper local rather than reaching for
// Intl.NumberFormat so the formatter doesn't bring in locale
// state on a customer-facing page (we want consistent rendering
// regardless of the visitor's locale).
function formatInteger(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

// Percent formatter dedicated to the KPI strip. Always two
// decimals (or "100%" exact) so the sub-line has predictable
// width regardless of which number the backend returns.
function formatPercentExact(pct: number): string {
  if (pct >= 100) return "100%"
  if (pct < 0) return "0%"
  return `${pct.toFixed(2)}%`
}
